/**
 * Recently viewed products — loaded directly by the section (not via lazy catalog).
 */
(function () {
  if (customElements.get('recently-viewed-products')) return;

  function localeHref(url) {
    if (window.theme && typeof theme.localeAwareHref === 'function') {
      return theme.localeAwareHref(url);
    }
    return url;
  }

  function readStore() {
    const name = '_halo_recently_viewed';
    try {
      const raw = JSON.parse(localStorage.getItem(name) || '[]');
      return Array.isArray(raw) ? raw : [];
    } catch (e) {
      localStorage.removeItem(name);
      return [];
    }
  }

  function writeStore(list) {
    localStorage.setItem('_halo_recently_viewed', JSON.stringify(list.slice(0, 25)));
  }

  function normalizeEntries(raw) {
    const out = [];
    for (const item of raw) {
      if (typeof item === 'number' && Number.isFinite(item) && item > 0) {
        out.push({ id: item, handle: '' });
      } else if (item && typeof item === 'object') {
        const id = parseInt(String(item.id), 10);
        if (!Number.isFinite(id) || id <= 0) continue;
        out.push({ id, handle: String(item.handle || '').trim() });
      }
    }
    return out;
  }

  class RecentlyViewedProducts extends HTMLElement {
    connectedCallback() {
      if (this._booted) return;
      this._booted = true;

      // Parsing fetched cards and initializing Swiper is substantial work.
      // Keep it out of the PDP critical path until this section approaches.
      if ('IntersectionObserver' in window) {
        this._viewportObserver = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this._isNearViewport = true;
            this._viewportObserver.disconnect();
            this.startLoading();
          },
          { rootMargin: '600px 0px' }
        );
        this._viewportObserver.observe(this);
      } else {
        this._isNearViewport = true;
        this.startLoading();
      }
    }

    disconnectedCallback() {
      this._viewportObserver?.disconnect();
      clearTimeout(this._retryTimer);
      clearTimeout(this._retryTimer2);
    }

    startLoading() {
      if (this._loadingScheduled) return;
      this._loadingScheduled = true;
      const start = () => this.load();
      queueMicrotask(start);
      this._retryTimer = setTimeout(start, 500);
      this._retryTimer2 = setTimeout(start, 1500);
    }

    markEmpty() {
      this.classList.add('hidden');
      this.removeAttribute('data-pending-load');
      const section = this.closest('.shopify-section');
      if (section) section.style.display = 'none';
    }

    markLoaded() {
      this.classList.add('recently-viewed--loaded');
      this.classList.remove('hidden');
      this.removeAttribute('data-pending-load');
      const section = this.closest('.shopify-section');
      if (section) section.style.display = '';
    }

    getUrl() {
      const limit = parseInt(this.dataset.limit, 10) || 8;
      const normalized = normalizeEntries(readStore());
      const cur = this.dataset.productId ? parseInt(this.dataset.productId, 10) : NaN;
      let entries = normalized;
      if (Number.isFinite(cur) && cur > 0) {
        entries = normalized.filter((e) => e.id !== cur);
      }
      const slice = entries.slice(0, limit);
      if (!slice.length) return null;
      const q = slice.map((e) => `id:${e.id}`).join(' OR ');
      const base = this.dataset.url;
      if (!base || !q) return null;
      return base + encodeURIComponent(q);
    }

    forceSwiperInit() {
      if (typeof theme !== 'undefined' && typeof theme.reinitSwiperComponentsWithin === 'function') {
        // Only reinit once per successful fetch
      }
      this.querySelectorAll('swiper-component').forEach((el) => {
        if (typeof el.initializeSwiper === 'function' && !el.initSwiper) {
          try {
            el.initializeSwiper();
          } catch (err) {
            console.warn('[recently-viewed] swiper init', err);
          }
        }
      });
      this.querySelectorAll('.swiper').forEach((el) => {
        el.swiper?.update?.();
      });
    }

    load() {
      if (!this._isNearViewport) return;
      if (this._loading || this.classList.contains('recently-viewed--loaded')) return;

      const url = this.getUrl();
      if (!url) {
        // Keep pending briefly on first paint; hide for good after retries settle.
        if (this._emptyAttempts == null) this._emptyAttempts = 0;
        this._emptyAttempts += 1;
        if (this._emptyAttempts >= 3) this.markEmpty();
        return;
      }

      this._loading = true;
      this.classList.remove('hidden');

      fetch(localeHref(url), { credentials: 'same-origin' })
        .then((response) => {
          if (!response.ok) throw new Error(`Recently viewed ${response.status}`);
          return response.text();
        })
        .then((text) => {
          const doc = new DOMParser().parseFromString(text, 'text/html');
          let remote = doc.querySelector('recently-viewed-products');
          if (!remote) {
            const wrap = doc.querySelector('[id^="shopify-section-"]');
            remote = wrap?.querySelector('recently-viewed-products');
          }
          if (!remote) {
            this.markEmpty();
            return;
          }

          this.innerHTML = remote.innerHTML;

          if (typeof theme !== 'undefined' && typeof theme.reinitSwiperComponentsWithin === 'function') {
            theme.reinitSwiperComponentsWithin(this);
          }

          const bump = () => this.forceSwiperInit();
          requestAnimationFrame(bump);
          setTimeout(bump, 50);
          setTimeout(bump, 300);
          this.querySelectorAll('img').forEach((img) => {
            if (!img.complete) img.addEventListener('load', bump, { once: true });
          });

          const hasProducts =
            this.querySelector(
              ".grid__item:not(.recently-viewed-products__skeleton-item) a[href*='/products/']"
            ) ||
            this.querySelector(
              '.grid__item:not(.recently-viewed-products__skeleton-item) [data-product-card-id]'
            ) ||
            this.querySelector(
              '.grid__item:not(.recently-viewed-products__skeleton-item) img[src]'
            );

          if (hasProducts) {
            this.markLoaded();
          } else {
            this.markEmpty();
          }

          if (typeof initMoreSwatchButtons === 'function') {
            initMoreSwatchButtons(this);
          }
        })
        .catch((err) => {
          console.error('[recently-viewed]', err);
          this.markEmpty();
        })
        .finally(() => {
          this._loading = false;
        });
    }
  }

  customElements.define('recently-viewed-products', RecentlyViewedProducts);

  // Collection/search: remember product cards when clicked
  document.addEventListener('click', function (e) {
    if (document.querySelector('product-info')) return;
    const link = e.target.closest("a[href*='/products/']");
    if (!link) return;
    const card = link.closest('[data-product-id]');
    if (!card || !card.dataset.productId) return;

    const id = parseInt(card.dataset.productId, 10);
    if (!id) return;

    let handle = '';
    try {
      const m = new URL(link.href, window.location.origin).pathname.match(/\/products\/([^/]+)/);
      if (m) handle = decodeURIComponent(m[1]);
    } catch (_) {}

    let list = normalizeEntries(readStore()).filter((x) => x.id !== id);
    list.unshift(handle ? { id, handle } : { id });
    writeStore(list);
  });
})();
